import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { StringFormatter } from '@repo/toolkit/string'
import { toFullName } from '@repo/toolkit/to-full-name'
import Badge from '@repo/ui/badge'
import { UserTwoIcon } from '@repo/ui/icons'
import InformationBox from '@repo/ui/information-box'
import InnerBox from '@repo/ui/inner-box'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'

import { getEnrollment } from '../../../api/enrollments/get-enrollment'
import { updateEnrollment } from '../../../api/enrollments/update-enrollment'
import LoadingScreen from '../../../components/elements/loading/loading-screen'
import ClientEnrollmentChildProfileForm from '../../../components/pages/client-space/child-profile/client-enrollment-child-profile-form'
import {
  mapEnrollmentChildInputToEnrollmentRequest,
  mapEnrollmentExpandedToEnrollmentChildInput
} from '../../../components/pages/client-space/child-profile/utils'
import ClientSpacePageTitle from '../../../components/pages/client-space/client-space-page-title'
import ClientEnrollmentProfileCard from '../../../components/pages/client-space/home/client-enrollment-profile-card'
import useEnrollment from '../../../lib/hooks/use-enrollment'
import { useSecureFile } from '../../../lib/hooks/use-secure-file'
import { EnrollmentInformationStatus } from '../../../lib/types/enums/enrollment/enrollment-information-status.enum'
import { enrollmentProfileFields } from '../../../lib/types/fields/enrollments/enrollment'
import { EnrollmentInput } from '../../../lib/types/models/enrollment/enrollment-input'
import { UpdateInput } from '../../../lib/types/utils'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'
import { withFrenchDe } from '../../../lib/utils/functions/user.functions'
import ClientInformationChildProfileView from './client-information-child-profile-view'

const ClientInformationChildProfile = () => {
  const { enrollment } = useEnrollment()

  const { url } = useSecureFile(enrollment?.secureIdentityPicture?.uploadId)

  const { data: enrollmentProfile, refetch } = useQuery({
    queryKey: ['enrollment', enrollment.id, enrollmentProfileFields],
    queryFn: () => {
      if (!enrollment.id) throw new Error('Enrollment ID is required')
      return getEnrollment(Number(enrollment.id), enrollmentProfileFields)
    },
    enabled: !!enrollment.id
  })

  const scrollToTop = () => {
    const element = document.getElementById('enrollment-container')
    if (!element) return

    const rect = element.getBoundingClientRect()
    const isInView = rect.top >= 0 && rect.top <= window.innerHeight

    if (!isInView) element.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const { mutateAsync } = useMutation({
    mutationFn: (input: UpdateInput<EnrollmentInput>) => updateEnrollment(input, ['id']),
    onError: showAxiosError,
    onSuccess: () => {
      toast.success('Profil de l’enfant mis à jour avec succès')
      refetch()
      setTimeout(() => {
        scrollToTop()
      }, 500)
    }
  })

  const formIsSubmitted =
    enrollmentProfile?.childInfoStatus !== EnrollmentInformationStatus.PENDING

  if (!enrollmentProfile) return <LoadingScreen />

  return (
    <div id="enrollment-container" className="flex flex-col gap-4">
      <ClientSpacePageTitle
        icon={<UserTwoIcon color={tailwindColors.primary.dark.DEFAULT} />}
        title="Profil de mon enfant - Dossier complémentaire"
        subTitlePosition="beside"
        subtitle={
          formIsSubmitted ? (
            <Badge
              variant="success"
              size="medium"
              text="Merci, nous avons bien reçu le dossier complémentaire de votre enfant. Il ne vous est plus possible de le modifier. Si vous vous rendez compte d’un problème avec les informations ci-dessous, merci de nous contacter par téléphone au 01.42.67.75.75."
            />
          ) : undefined
        }
      />

      {formIsSubmitted ? (
        <ClientInformationChildProfileView
          enrollmentPicturePath={url ?? undefined}
          childName={toFullName(enrollment.childFirstName, enrollment.childLastName, {
            options: { mode: 'lastname-firstname' }
          })}
          enrollment={enrollmentProfile}
          refetch={refetch}
        />
      ) : (
        <>
          <InformationBox isHiddenOnMobile={false}>
            <Typography variant="secondary-body" color={tailwindColors.grey.DEFAULT}>
              Ce dossier complémentaire est à compléter rapidement et avec le plus de précisions
              possibles. Tant que vous n’aurez pas complété tous les champs et cliqué sur «
              <strong className="text-black"> Valider et envoyer à LEC </strong>», il ne nous sera
              pas possible de sélectionner la famille-hôtesse{' '}
              {withFrenchDe(
                enrollment.childFirstName
                  ? StringFormatter.capitalize(enrollment.childFirstName)
                  : ''
              )}
              .
            </Typography>
          </InformationBox>
          <ClientEnrollmentProfileCard
            enrollment={enrollment}
            maxImageSizeMB={16}
            disabled={enrollment.identityPictureLocked}
            refetch={refetch}
          >
            <div className="flex flex-col gap-1.5">
              <Typography variant="text" color={tailwindColors.black.DEFAULT}>
                Ajouter une photo d'identité récente de votre enfant
              </Typography>
              <InnerBox className="bg-white-active p-2 rounded-sm">
                <Typography variant="label-medium" color={tailwindColors.grey.DEFAULT}>
                  Cette photo devra être de type photo d'identité (portrait de face où le visage
                  apparait clairement et de façon rapprochée, sur fond blanc, sans lunettes de
                  soleil ni bonnet, casquette ou chapeau). Les formats autorisés sont jpg, jpeg et
                  png. La taille maximale autorisée est 16MO.
                </Typography>
              </InnerBox>
            </div>
          </ClientEnrollmentProfileCard>

          <ClientEnrollmentChildProfileForm
            enrollmentId={enrollment.id}
            enrollment={enrollment}
            isTermsAndConditionsAccepted={!!enrollmentProfile.termsAcceptedDate}
            defaultValues={mapEnrollmentExpandedToEnrollmentChildInput(enrollmentProfile)}
            handleSubmit={async (values, isLongVersion) => {
              const updatedValues = mapEnrollmentChildInputToEnrollmentRequest(
                values,
                isLongVersion
              )
              await mutateAsync({ id: enrollment.id, ...updatedValues })
            }}
          />
        </>
      )}
    </div>
  )
}

export default ClientInformationChildProfile
